import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import { API_ENDPOINT } from "../../config";
import { BookCourierResponse, ShipmentCourierStatus, ShipmentSummary } from "../../interfaces";
import { fulfilmentApi } from "../fulfilment/fulfilment.api";
import { BulkOrderResultRow } from "../manifest/manifest.api";

export type BookCourierItem = {
  sku_item_id: string;
  awb_number?: string;
  delivery_partner?: string;
};

export type BookCourierPayload = {
  sku_code?: string;
  carrier?: "MANUAL" | "DELHIVERY" | "EKART";
  dispatch_date?: string;
  delivery_partner?: string;
  items: BookCourierItem[];
};

export type BulkUpdateShipmentStatusPayload = {
  shipment_ids: string[];
  status: ShipmentCourierStatus;
  event_at: string;
};

export type BulkUpdateShipmentStatusResponse = {
  status: ShipmentCourierStatus;
  event_at: string;
  updated_count: number;
  shipments: Array<
    ShipmentSummary & { previous_status: ShipmentCourierStatus | null }
  >;
};

export type CreatePickupRequestPayload = {
  sku_item_ids: string[];
  pickup_date: string;
  pickup_time: string;
};

export type CreatePickupRequestResponse = {
  carrier?: "DELHIVERY" | "EKART";
  pickup_id: string;
  pickup_date: string;
  pickup_time: string;
  expected_package_count: number;
  item_count: number;
  /** Ekart pickup is initiated by shipment create, not a second carrier call. */
  initiated_with_shipment?: boolean;
};

export type BulkBookCourierResponse = {
  succeeded: Array<{
    cart_order_id: string;
    item_count: number;
    package_count: number;
    awb_numbers: string[];
  }>;
  skipped: BulkOrderResultRow[];
  failed: BulkOrderResultRow[];
  success_count: number;
  skipped_count: number;
  failed_count: number;
};

export const shipmentApi = createApi({
  reducerPath: "shipmentApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_ENDPOINT + "/admin/shipments",
    credentials: "include",
  }),
  endpoints: (build) => ({
    bookCourier: build.mutation<BookCourierResponse, BookCourierPayload>({
      query: (body) => ({
        url: "/book",
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            fulfilmentApi.util.invalidateTags([
              "FulfilmentPayments",
              "FulfilmentSummary",
              "FulfilmentPickups",
            ]),
          );
        } catch {
          /* toast handled by caller */
        }
      },
    }),
    createPickupRequest: build.mutation<
      CreatePickupRequestResponse,
      CreatePickupRequestPayload
    >({
      query: (body) => ({
        url: "/pickup-request",
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            fulfilmentApi.util.invalidateTags([
              "FulfilmentPayments",
              "FulfilmentSummary",
              "FulfilmentPickups",
            ]),
          );
        } catch {
          /* toast handled by caller */
        }
      },
    }),
    bulkUpdateShipmentStatus: build.mutation<
      BulkUpdateShipmentStatusResponse,
      BulkUpdateShipmentStatusPayload
    >({
      query: (body) => ({
        url: "/bulk-status",
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            fulfilmentApi.util.invalidateTags([
              "FulfilmentPayments",
              "FulfilmentSummary",
              "FulfilmentPickups",
            ]),
          );
        } catch {
          /* toast handled by caller */
        }
      },
    }),

    bulkBookCourier: build.mutation<
      BulkBookCourierResponse,
      { cart_order_ids: string[]; carrier?: "DELHIVERY" | "EKART" | "MANUAL"; dispatch_date?: string }
    >({
      query: (body) => ({
        url: "/book-bulk",
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            fulfilmentApi.util.invalidateTags([
              "FulfilmentPayments",
              "FulfilmentSummary",
              "FulfilmentPickups",
            ]),
          );
        } catch {
          /* toast handled by caller */
        }
      },
    }),
  }),
});

export const {
  useBookCourierMutation,
  useCreatePickupRequestMutation,
  useBulkUpdateShipmentStatusMutation,
  useBulkBookCourierMutation,
} = shipmentApi;
